import { StyleRulesCallback, Theme, Typography, WithStyles, withStyles } from '@material-ui/core';
import * as React from 'react';
import { pure } from 'recompose';
import { isDate, isNumber, needsToBeOne, required, Validator } from '../../../shared/validation';
import FormTextField from '../../../components/FormTextField';
import { Field } from 'redux-form';
import { BpM } from '../../../shared/brakepoints';
import FormSelectField from '../../../components/FormSelectField';
import FormDateField from '../../../components/FormDateField';
import { defaultMemoize } from 'reselect';
import Translator from '../../../components/Translator';

type CssClasses = 'fieldContainer' | 'fullRow' | 'bigRow';
const styles: StyleRulesCallback<CssClasses> = (theme: Theme) => ({
  fieldContainer: {
    display: 'flex',
    flexFlow: 'column',
    '& > *': {
      flex: '0 0 100%',
    },
    [BpM()]: {
      display: 'flex',
      margin: '0px -5px 0px',
      width: '500px',
      flexFlow: 'row wrap',
      alignItems: 'flex-end',
      '& > *': {
        flex: '0 0 250px',
        paddingLeft: '5px',
        paddingRight: '5px',
        maxWidth: '250px',
      },
    },
  },
  fullRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
  },
  bigRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
    [BpM()]: {
      '& > *': {
        width: '320px',
        paddingRight: '10px',
      },
    },
  },
});

interface Props {}

class BudgetDisclosureFormData extends React.Component<Props & WithStyles<CssClasses>> {
  private translatedRequire = defaultMemoize((translation: string) => {
    return required(translation);
  });
  private translatedIsNumber = defaultMemoize((translation: string) => {
    return isNumber(translation);
  });
  private translatedNeedsToBeOne = defaultMemoize((translation: string) => {
    return needsToBeOne(translation);
  });

  private justRequired = defaultMemoize((requiredValidator: Validator) => {
    return [requiredValidator];
  });

  private requireNumber = defaultMemoize((isNumberValidator: Validator) => {
    return [isNumberValidator];
  });

  private needsToBeOne = defaultMemoize((needsToBeOneValidator: Validator) => {
    return [needsToBeOneValidator];
  });

  render() {
    let { classes } = this.props;
    return (
      <Translator>
        {translate => {
          const req = this.translatedRequire(translate('missing'));
          const num = this.translatedIsNumber(translate('invalidNumber'));
          const one = this.translatedNeedsToBeOne(translate('confirm'));
          const justRequired = this.justRequired(req);
          const requiredNumber = this.requireNumber(num);
          const confirm = this.needsToBeOne(one);
          return (
            <>
              <Typography variant={'h6'}>{translate('budgetTitle')}</Typography>

              <div className={classes.fieldContainer}>
                <Field
                  name="partner1AlimonyPersons"
                  requireMark
                  component={FormTextField}
                  validate={requiredNumber}
                  label={translate('budgetAlimonyPersonCount')}
                  fullWidth={true}
                />
                <Field
                  name="partner1AlimonyChildren"
                  requireMark
                  component={FormTextField}
                  validate={requiredNumber}
                  label={translate('budgetAlimonyChildrenCount')}
                  fullWidth={true}
                />

                <Field
                  name="partner1JobType"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('budgetJobType')}
                  fullWidth={false}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: '0', text:  translate('jobTypeEmployee') },
                    { value: '1', text:  translate('jobTypeWorker') },
                    { value: '2', text:  translate('jobTypeFreelancer') },
                    { value: '3', text:  translate('jobTypeSelfEmployed') },
                    { value: '4', text:  translate('jobTypeClerk') },
                    { value: '5', text:  translate('jobTypePensioner') },
                    { value: '6', text:  translate('jobTypeUnemployed') },
                    { value: '7', text:  translate('jobTypePupil') },
                    { value: '8', text:  translate('jobTypeStudent') },
                    { value: '9', text:  translate('jobTypeApprentice') },
                    { value: '10', text: translate('jobTypeAtHome') },
                    { value: '11', text: translate('jobTypeWithoutJob') },
                    { value: '12', text: translate('jobTypeIndependent') },
                  ]}
                />
                <Field
                  name="partner1ProbationaryPeriod"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('budgetProbationaryPeriod')}
                  fullWidth={true}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: '0', text: translate('no') },
                    { value: '1', text: translate('yes') },
                  ]}
                />
                <Field
                  name="partner1FixedTermEmploymentContract"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('budgetFixedTermEmploymentContract')}
                  fullWidth={true}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: '0', text: translate('no') },
                    { value: '1', text: translate('yes') },
                  ]}
                />
                <Field
                  name="partner1EmploymentContractEndStamp"
                  component={FormDateField}
                  label={translate('budgetEmploymentContractEndStamp')}
                  fullWidth={true}
                />
                <Field
                  name="partner1NetIncome"
                  requireMark
                  component={FormTextField}
                  validate={requiredNumber}
                  endAdornment={'€'}
                  alignRight={true}
                  label={translate('budgetNetIncome')}
                  fullWidth={true}
                />
                <Field
                  name="partner1AdditionalIncome"
                  requireMark
                  component={FormTextField}
                  validate={requiredNumber}
                  endAdornment={'€'}
                  alignRight={true}
                  label={translate('budgetAdditionalIncome')}
                  fullWidth={true}
                />
                <div className={classes.fullRow}>
                  <Field
                    name="partner1QualityQuestion1"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('budgetQualityQuestion1')}
                    fullWidth={true}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: '0', text: translate('no') },
                      { value: '1', text: translate('yes') },
                    ]}
                  />
                </div>
                <div className={classes.fullRow}>
                  <Field
                    name="partner1QualityQuestion2"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('budgetQualityQuestion2')}
                    fullWidth={true}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: '0', text: translate('no') },
                      { value: '1', text: translate('yes') },
                    ]}
                  />
                </div>
                <div className={classes.fullRow}>
                  <Field
                    name="partner1QualityQuestion3"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('budgetQualityQuestion3')}
                    fullWidth={true}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: '0', text: translate('no') },
                      { value: '1', text: translate('yes') },
                    ]}
                  />
                </div>
              </div>
            </>
          );
        }}
      </Translator>
    );
  }
}

export default pure(withStyles(styles)(BudgetDisclosureFormData));



// WEBPACK FOOTER //
// ./src/routes/disclosure/components/BudgetDisclosureFormData.tsx